import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMasterDataStore } from "../stores/masterDataStore";
import { loadAllMasterData } from "../services/masterDataService";

/**
 * Hook para cargar y gestionar datos maestros globales.
 * Se ejecuta automáticamente al montar la aplicación.
 * Usa React Query para fetch + Zustand como SoT (decisión D2).
 *
 * Idempotencia del sync RQ→Zustand:
 *   - `enabled: !isLoaded` previene refetch tras carga exitosa.
 *   - Los 3 useEffect solo se activan una vez por ciclo de vida.
 *   - Sin riesgo de loops.
 */
export const useMasterData = () => {
  const {
    countries,
    genders,
    documentTypes,
    isLoaded,
    setCountries,
    setGenders,
    setDocumentTypes,
    setLoading,
    setError,
    setLoaded,
  } = useMasterDataStore();

  // Query para cargar todos los datos maestros - siempre se ejecuta en cada inicio
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ["masterData"],
    queryFn: loadAllMasterData,
    enabled: !isLoaded,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  // Sincronizar estado de React Query con Zustand store
  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  useEffect(() => {
    if (isError && error) {
      setError(error.message || "Error loading master data");
      setLoaded(false);
    } else {
      setError(null);
    }
  }, [isError, error, setError, setLoaded]);

  useEffect(() => {
    if (isSuccess && data) {
      setCountries(data.countries);
      setGenders(data.genders);
      setDocumentTypes(data.documentTypes);
      setLoaded(true);
      setError(null);
    }
  }, [
    isSuccess,
    data,
    setCountries,
    setGenders,
    setDocumentTypes,
    setLoaded,
    setError,
  ]);

  return {
    countries,
    genders,
    documentTypes,
    isLoading,
    isLoaded,
    error,
    isError,
  };
};

/**
 * Hook para obtener helpers de búsqueda de datos maestros
 * Útil para mapear IDs a labels en componentes
 */
export const useMasterDataMappers = () => {
  const {
    getCountryById,
    getGenderById,
    getDocumentTypeById,
    getGenderByLetter,
    getDocumentTypeByInitials,
  } = useMasterDataStore();

  return {
    // Mappers por ID
    getCountryName: (id: number | null) => getCountryById(id)?.country || "",
    getGenderName: (id: number | null) => getGenderById(id)?.gender || "",
    getDocumentTypeName: (id: number | null) =>
      getDocumentTypeById(id)?.identification_name || "",
    getDocumentTypeInitials: (id: number | null) =>
      getDocumentTypeById(id)?.identification_initials || "",

    // Mappers por código/letra
    getGenderNameByLetter: (letter: string) =>
      getGenderByLetter(letter)?.gender || letter,
    getDocumentTypeNameByInitials: (initials: string) =>
      getDocumentTypeByInitials(initials)?.identification_name || initials,

    // Funciones directas para casos avanzados
    getCountryById,
    getGenderById,
    getDocumentTypeById,
    getGenderByLetter,
    getDocumentTypeByInitials,
  };
};
